import React, { Suspense, lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import {
  HistoryListener,
  NavigationConfirmModal,
} from 'react-router-navigation-confirm';

import { useCountry } from '../contexts/CountryContext';
import PageLoading from '../components/PageLoading';
import useAuthRecoil from '../hooks/useAuthRecoil';
import ProtectedRoute from './ProtectedRoute';

import Layout from '../components/Layout';
import Supports from '../pages/Supports';
import Companies from '../pages/Companies';
import Dass from '../pages/Dass';

const Home = lazy(() => import('../pages/Home'));
const Projects = lazy(() => import('../pages/Projects'));
const ProjectDetails = lazy(() => import('../pages/ProjectDetails'));
const InvestmentContainer = lazy(() => import('../pages/InvestmentContainer'));
const MercadoPagoContainer = lazy(() =>
  import('../pages/MercadoPagoContainer'),
);
const MyInvestments = lazy(() => import('../pages/MyInvestments'));
const ReferralsProgram = lazy(() => import('../pages/ReferralsProgram'));
const HowItWorks = lazy(() => import('../pages/HowItWorks'));
const About = lazy(() => import('../pages/About'));
const Error404 = lazy(() => import('../pages/Error404'));
const ConstructionPage = lazy(() => import('../pages/Constrution'));
const Profile = lazy(() => import('../pages/Profile'));
const TermsConditions = lazy(() => import('../pages/TermsConditions'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const FAQIndex = lazy(() => import('../pages/FAQIndex'));
const FAQCategory = lazy(() => import('../pages/FAQCategory'));
const FAQDetail = lazy(() => import('../pages/FAQDetail'));
const ExposedPersonPolicy = lazy(() => import('../pages/ExposedPersonPolicy'));
const MercadoLibre = lazy(() => import('../pages/MercadoLibre'));
const Blog = lazy(() => import('../pages/Blog'));
const Article = lazy(() => import('../pages/Article'));
const Products = lazy(() => import('../pages/Products'));
const ProductTypes = lazy(() => import('../pages/ProductTypes'));
const Tuluka = lazy(() => import('../pages/Tuluka'));
const Goals = lazy(() => import('../pages/Goals'));
const Investments = lazy(() => import('../pages/Investments'));
const CreateInvestment = lazy(() => import('../pages/CreateInvestment'));
const CreateGoalPage = lazy(() => import('../pages/CreateGoalPage'));
const PageInvestmentDetails = lazy(() =>
  import('../pages/PageInvestmentDetails'),
);
const PageGoalDetails = lazy(() => import('../pages/PageGoalDetails'));
const PageGoalDetailsActivity = lazy(() =>
  import('../pages/PageGoalDetailsActivity'),
);
const PageGoalInvestment = lazy(() =>
  import('../pages/PageCreateGoalInvestment'),
);
const PageManualReinvestment = lazy(() =>
  import('../pages/PageCreateManualReinvestment'),
);
const Boost = lazy(() => import('../pages/Boost'));
const NaranjaX = lazy(() => import('../pages/NaranjaX'));

const AppRouter = () => {
  return (
    <Layout>
      <>
        <HistoryListener>
          <NavigationConfirmModal
            confirmText={'Continuar'}
            cancelText={'Cancelar'}
            buttonConfirmClassName={'cancel-button'}
            when={(...args) => {
              if (args[0].state?.update === true) {
                return true;
              }
              return false;
            }}
          >
            <div>
              <h2>¿Esta seguro que desea salir?</h2>
              <br />
              <p>Los datos sin guardar se perderan</p>
            </div>
          </NavigationConfirmModal>
        </HistoryListener>
      </>
      <Suspense fallback={<PageLoading />}>
        <Switch>
          <MyRoutes />
        </Switch>
      </Suspense>
    </Layout>
  );
};

const MyRoutes = () => {
  const { countryPrefix } = useCountry();
  const location = useLocation();

  const {
    auth: { isAuth },
  } = useAuthRecoil();

  if (!countryPrefix || countryPrefix === '/') {
    return <Redirect to={`/ar/`} />;
  } else {
    if (location.pathname === '/' || location.pathname === `/ar`) {
      return <Redirect to={`/ar/`} />;
    }
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <Switch>
        <Route exact path={`/ar`}>
          <NaranjaX />
        </Route>

        <Redirect
          from="/proyecto_inmobiliario"
          to={`/ar/proyectos-inmobiliarios/`}
        />

        <Redirect
          exact
          from="/proyectos-inmobiliarios/proyecto/"
          to={`/ar/proyectos-inmobiliarios/`}
        />
        <Route path={`/ar/proyectos-inmobiliarios/:id/inversion`}>
          <InvestmentContainer />
        </Route>
        <Route path={`/ar/proyectos-inmobiliarios/:id/mercadopago/:status`}>
          <MercadoPagoContainer />
        </Route>

        {isAuth && (
          <Route exact path={`/ar/mi-perfil`}>
            <Profile />
          </Route>
        )}

        <Route exact path={`/ar/productos`}>
          <Products />
        </Route>

        <Route exact path={`/ar/productos/:productType`}>
          <ProductTypes />
        </Route>

        <Route exact path={`/ar/proyectos-inmobiliarios/:filters?`}>
          <Projects />
        </Route>

        <Route
          // exact
          path={`/ar/proyectos-inmobiliarios/proyecto/:slug`}
        >
          <ProjectDetails />
        </Route>

        <ProtectedRoute
          exact
          path={`/ar/dashboard/inversiones/detalle-inversion/:id`}
          isAuth={isAuth}
        >
          <PageInvestmentDetails />
        </ProtectedRoute>

        <ProtectedRoute
          path={`/ar/dashboard/inversiones/nueva-inversion`}
          isAuth={isAuth}
        >
          <CreateInvestment />
        </ProtectedRoute>

        <ProtectedRoute
          exact={true}
          path={`/ar/dashboard/inversiones/nueva-reinversion`}
          isAuth={isAuth}
        >
          <PageManualReinvestment />
        </ProtectedRoute>

        <ProtectedRoute path={`/ar/dashboard/inversiones`} isAuth={isAuth}>
          <Investments />
        </ProtectedRoute>

        <ProtectedRoute
          exact={true}
          path={`/ar/dashboard/metas/nueva-inversion-meta/:id/:method?`}
          isAuth={isAuth}
        >
          <PageGoalInvestment />
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path={`/ar/dashboard/metas/detalle-meta/:id/inversion`}
          isAuth={isAuth}
        >
          <PageGoalDetailsActivity />
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path={`/ar/dashboard/metas/detalle-meta/:id`}
          isAuth={isAuth}
        >
          <PageGoalDetails />
        </ProtectedRoute>

        <ProtectedRoute path={`/ar/dashboard/metas/nueva-meta`} isAuth={isAuth}>
          <CreateGoalPage />
        </ProtectedRoute>

        <Redirect
          from={`/ar/metas`}
          to={{
            pathname: `/ar/productos`,
            hash: '#metas',
            search: location.search && location.search,
          }}
        />

        <ProtectedRoute path={`/ar/dashboard/metas`} isAuth={isAuth}>
          <Goals />
        </ProtectedRoute>

        <ProtectedRoute path={`/ar/dashboard/potenciar`} isAuth={isAuth}>
          <Boost />
        </ProtectedRoute>

        <ProtectedRoute path={`/ar/dashboard/potenciar`} isAuth={isAuth}>
          <Boost />
        </ProtectedRoute>

        <ProtectedRoute path={`/ar/dashboard`} isAuth={isAuth}>
          <MyInvestments />
        </ProtectedRoute>

        <Route exact path={`/ar/mi-perfil/referidos`}>
          <ReferralsProgram />
        </Route>

        {/*<ProtectedRoute
            path={`/ar/mi-perfil/inversiones/:slug?`}
            isAuth={isAuth}
          >
            <Profile />
          </ProtectedRoute>*/}

        <Route path={`/ar/terminos-y-condiciones`}>
          <TermsConditions />
        </Route>

        <Route path={`/ar/politica-privacidad`}>
          <PrivacyPolicy />
        </Route>

        <Route path={`/ar/preguntas-frecuentes/:categorySlug/:questionSlug`}>
          <FAQDetail />
        </Route>

        <Route path={`/ar/preguntas-frecuentes/:categorySlug`}>
          <FAQCategory />
        </Route>

        <Route exact path={`/ar/preguntas-frecuentes`}>
          <FAQIndex />
        </Route>

        <Route path={`/ar/politica-persona-expuesta-politicamente`}>
          <ExposedPersonPolicy />
        </Route>

        <Route path={`/ar/mercadolibre`}>
          <MercadoLibre />
        </Route>

        <Route path={`/ar/tuluka`}>
          <Tuluka />
        </Route>

        <Route path={`/ar/nosotros`}>
          <About />
        </Route>

        <Route path={`/ar/como-funciona`}>
          <HowItWorks />
        </Route>

        <Route path={`/ar/proyectos-inmobiliarios`}>
          <Projects />
        </Route>

        {/* <Route exact path={`/ar/metas`}>
            <Goals />
          </Route> */}

        <Route exact path={`/ar/simple-academy`}>
          <Blog />
        </Route>
        <Route exact path={`/ar/simple-academy/:slug/:id`}>
          <Article />
        </Route>

        <Route exact path={`/ar/construccion`}>
          <ConstructionPage />
        </Route>

        <Route exact path={`/ar/respaldo`}>
          <Supports />
        </Route>

        <Route exact path={`/ar/empresas`}>
          <Companies />
        </Route>

        <Route exact path={`/ar/dass`}>
          <Dass />
        </Route>

        <Route exact path={`/ar/naranjax`}>
          <NaranjaX />
        </Route>

        <Route>
          <Error404 />
        </Route>
      </Switch>
    </Suspense>
  );
};

export default AppRouter;
